import type { MessageKey } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ 3 Nov 2026 · A FILE ON AN INVOICE, AND WHY IT DID NOT GO.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The invoice page collapsed every refusal to `'upload_failed'` and then threw the answer away —
 * so with the virus scanner down (`scanner_unavailable`) an upload simply did nothing. This posts
 * the file, and on a refusal returns the envelope's `code` (rule 5: branch on `code`), which
 * `attachErrorKey` turns into the sentence `contract-detail.tsx` already says for the same codes.
 * The period page's invoice rows use it too.
 */
export async function attachToInvoice(
  request: (path: string, init?: RequestInit) => Promise<Response>,
  invoiceId: string,
  file: File,
): Promise<string | null> {
  const form = new FormData();
  form.set('file', file);
  const response = await request(`/files?target=invoice&targetId=${invoiceId}`, {
    method: 'POST',
    body: form,
  }).catch(() => null);
  if (response === null) return 'unavailable';
  if (response.ok) return null;
  const envelope = (await response.json().catch(() => null)) as {
    error?: { code?: string };
  } | null;
  return envelope?.error?.code ?? 'unavailable';
}

/** The four codes C22 made distinct, each with its own sentence — the contract's own map. */
const ATTACH_ERROR: Readonly<Record<string, MessageKey>> = {
  file_too_large: 'task.attachment.tooLarge',
  file_infected: 'task.attachment.infected',
  scanner_unavailable: 'task.attachment.scannerDown',
  unsupported_file_type: 'task.attachment.failed',
};

export function attachErrorKey(code: string): MessageKey {
  return ATTACH_ERROR[code] ?? 'task.attachment.failed';
}
